import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as Sentry from '@sentry/browser';
import { CustomValidators } from 'ng2-validation';
import { MatomoTracker } from 'ngx-matomo';

import {
  ActionButton,
  ButtonType,
} from '../../../../shared/components/refactor-components/layouts/icp-onboarding/icp-onboarding.model';
import { BaseFormComponent } from '../../../../shared/components/refactor-components/base-form/base-form.component';
import { DirectionsEnum } from '../../../../shared/components/refactor-components/layouts/icp-splitted-screen/icp-splitted-screen.model';
import { IcpAlertService } from '../../../../shared/components/refactor-components/ui/icp-alert/icp-alert.service';
import { IcpCarouselModel } from '../../../../shared/components/refactor-components/ui/icp-carousel/carousel-images.model';
import { GlobalConfig } from '../../../../shared/configs/global-config';
import { Global } from '../../../../shared/constants/global.constant';
import { Messages } from '../../../../shared/constants/messages.constant';
import { AppRoutes } from '../../../../shared/constants/routes/routes.constant';
import { CacheService } from '../../../../shared/services/cache.service';
import { ContentLoadService } from '../../../../shared/services/content-load.service';
import { IntercomService } from '../../../../shared/services/intercom.service';
import { formatDateToSend } from '../../../../shared/utils/formatter';

import { ThemeSharedService } from '../../../../theme/services/theme.shared.service';

import { slideInOutAnimation } from '../../animations/router.animations';

import { UserMessages } from '../../constants/messages.constants';

import { NoOrganizationError } from '../../models/user.errors';
import { User } from '../../models/user.model';

import { AuthService } from '../../services/auth.service';
import { EmailConfirmationService } from '../../services/email-confirmation.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { LoginService } from '../../services/login.service';
import { UserWebService } from '../../services/user.web.service';

@Component({
  selector: 'login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  animations: [slideInOutAnimation],
  host: { '[@slideInOutAnimation]': '' },
})
export class LoginComponent extends BaseFormComponent {
  user: User;
  error: string = '';
  isUser: boolean = true;
  showLoading: boolean;
  errorAlert: boolean;
  acordoPending: any = { pending: false, id: '' };
  loginSuccessError: any;
  emailConfirmed: boolean = false;
  emailNotConfirmed: boolean = false;
  passwordShown: boolean = false;
  contrastSide: string = DirectionsEnum.LEFT;

  images: Array<IcpCarouselModel> = [
    {
      url: '/assets/img/login-carousel-01.svg',
      alt: 'Imagem carrossel login 1',
      caption: 'Potencialize o poder de <br> pagamento <b>dos seus pacientes.</b>',
    },
    {
      url: '/assets/img/login-carousel-02.svg',
      alt: 'Imagem carrossel login 2',
      caption: '<b>Antecipe já!</b> Recursos para <br> sua clínica sem gerar dívidas.',
    },
    {
      url: '/assets/img/login-carousel-03.svg',
      alt: 'Imagem carrossel login 3',
      caption:
        'Quer ter mais tempo para curtir a <br> vida? <b>Otimize seus atendimentos.</b>',
    },
  ];

  btnAcessar: ActionButton = {
    buttonType: ButtonType.Loading,
    text: 'Acessar',
    buttonClass: 'button-primary',
    showLoading: false,
  };

  btnCriarConta: ActionButton = {
    buttonType: ButtonType.Default,
    text: 'Criar uma conta',
    buttonClass: 'button-primary-outline',
    routerLink: '/onboarding/etapas',
  };

  actions: ActionButton[] = [this.btnAcessar];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private loginService: LoginService,
    private route: ActivatedRoute,
    private emailConfirmationService: EmailConfirmationService,
    private localStorage: LocalStorageService,
    private themeService: ThemeSharedService,
    private matomoTracker: MatomoTracker,
    private userService: UserWebService,
    private cacheService: CacheService,
    private contentLoadService: ContentLoadService,
    private intercomService: IntercomService,
    private icpAlertService: IcpAlertService,
    private auth: AuthService
  ) {
    super(
      { email: '', password: '' },
      {
        email: {
          required: Messages.EMAIL_REQUIRED,
          email: Messages.EMAIL_INVALID,
        },
        password: {
          required: Messages.PASSWORD_REQUIRED,
          minlength: Messages.PASSWORD_MINLENGTH,
        },
      },
      fb
    );
    this.user = new User();
  }

  ngOnInit() {
    const accessToken = this.localStorage.get('access_token');
    const user = this.localStorage.getUser();
    const urlToRedirect = this.localStorage.get('urlToRedirect');

    if (accessToken && user) {
      this.contentLoadService.setLoadingData([' ', true]);

      if (urlToRedirect) {
        this.router.navigate([urlToRedirect]);
      } else {
        this.handleRedirections(null);
      }
    } else {
      this.contentLoadService.setLoadingData(['', false]);
    }

    this.buildForm();

    this.route.params.subscribe((params) => {
      const emailConfirmationToken = params['email_confirmation_token'];
      if (emailConfirmationToken) {
        this.emailConfirmationService.confirmEmail(emailConfirmationToken).subscribe(
          (res) => {
            if (this.localStorage.getObject('user')) {
              this.themeService.setEmailConfirmed(true);
              const newUser = this.localStorage.getUser();
              newUser.data_confirmacao_email = formatDateToSend(new Date());
              this.localStorage.setObject('user', newUser);
            } else {
              this.emailConfirmed = true;
            }
          },
          (err) => {
            console.error(err);
            if (this.localStorage.getObject('user')) {
              this.themeService.setEmailNotConfirmed(true);
            } else {
              this.emailNotConfirmed = true;
            }
          }
        );
      }
    });
  }

  ngOnDestroy() {
    this.emailConfirmed = false;
    this.emailNotConfirmed = false;
  }

  initializeForm(): FormGroup {
    return this.fb.group({
      email: [
        this.user.email,
        Validators.compose([Validators.required, CustomValidators.email]),
      ],
      password: [
        this.user.password,
        Validators.compose([Validators.required, Validators.minLength(6)]),
      ],
    });
  }

  sendForm() {
    this.showLoading = true;
    const user = new User(
      null,
      null,
      this.form.value.email.toLowerCase(),
      this.form.value.password,
      null,
      null
    );
    return this.loginService.login(user).subscribe(
      (res) => {
        this.loginSuccess(res);
      },
      (res) => {
        this.handleResponseErrors(res);
      }
    );
  }

  loginSuccess(res) {
    const organizacao = res.organizacoes[0];
    const existsOrganizationActive =
      organizacao.organizacao_status_id === Global.ORGANIZACAO_ATIVA;

    if (!existsOrganizationActive) {
      this.btnAcessar.showLoading = false;
      this.icpAlertService.warn(
        'Sua organização não está ativa.<br/>Para mais informações contate nossa<br/>central de atendimento (11) 2199-6980',
        { autoClose: false }
      );
      this.router.navigate([AppRoutes.LOGOUT]);
      this.auth.logout();
      return;
    }

    this.initializeMatomo(res);
    this.cacheService
      .getRouterGuardData()
      .subscribe((res) => this.handleRedirections(res));
  }

  initializeMatomo(res) {
    this.matomoTracker.setUserId(res.id);
    this.matomoTracker.setCustomDimension(1, res.name);
    this.matomoTracker.setCustomDimension(2, res.organizacoes[0].name);
    if (
      res.organizacoes &&
      res.organizacoes[0].organizacao_status_id !== GlobalConfig.ORGANIZACAO_APROVADA
    ) {
      return this.router.navigate(['invite-queue']);
    }
    this.error = '';
  }

  getCobrancaAssinatura() {
    return this.cacheService.getCobrancaAssinatura();
  }

  handleRedirections(routerGuardData) {
    this.intercomService.init();
    let urlToRedirect = this.localStorage.get('urlToRedirect');
    const notBasicRoles =
      !this.localStorage.getRole().includes('iniciante') &&
      !this.localStorage.getRole().includes('basico');

    if (urlToRedirect) {
      urlToRedirect = urlToRedirect.replace(/"/g, '');
      window.location.href = `${window.location.origin}/#${urlToRedirect}`;
      window.localStorage.removeItem('urlToRedirect');
      return;
    }

    this.contentLoadService.setLoadingData(['', false]);

    if (this.localStorage.getUserPermissions().includes('dashboard') && notBasicRoles) {
      this.router.navigate(['pages/gestao-financeira/dashboard/resumo']);
      return;
    }

    this.router.navigate(['pages/simulador-vendas']);
  }

  handleResponseErrors(errors) {
    this.error = '';
    const status = errors.status;
    errors && Sentry.captureException(errors);

    if (status === 401) {
      this.error = 'Dados inválidos';
      this.showLoading = false;
      this.btnAcessar.showLoading = false;
      this.icpAlertService.error(this.error);
    }

    if (errors instanceof NoOrganizationError) {
      this.error = UserMessages.NO_ORGANIZATION;
      this.showLoading = false;
      this.btnAcessar.showLoading = false;
      this.icpAlertService.error(this.error);
    }
  }

  handleErrors() {}

  submitForm() {
    if (this.form.controls.email.value && !this.form.invalid) {
      this.btnAcessar.showLoading = true;
      this.onSubmit(this.form.value);
    }
  }
}
